import {CSSProperties} from "react";
import toast from "react-hot-toast";
import classNames from "classnames";
import {
    CompetitionToastPayload,
    CompetitionToastState,
    competitionToastFamily,
} from "@/types/CompetitionToastConfig";

/*
 * Imperative firing API for the competition notification family (kit §04
 * anatomy: severity rail, icon tile, title/message, timing bar, explicit
 * dismiss). Rides the existing react-hot-toast <Toaster> in PageWrapper —
 * no position is passed; the family renders wherever the app-wide Toaster
 * default puts it.
 *
 * Skin: when fired from a board page the surface's --game tokens are read
 * off the page root and set inline on the toast, because the Toaster portal
 * mounts outside the page root and inline page tokens don't cascade to it.
 */

const DEFAULT_DURATION_MS = 6000;

/** Page roots that carry the game-skin tokens, in lookup order */
const SKIN_ROOT_SELECTORS = [".leaderboard-detail-page"];

const SKIN_TOKENS = ["--game", "--game-deep"] as const;

function readSkinTokens(): CSSProperties {
    for (const selector of SKIN_ROOT_SELECTORS) {
        const root = document.querySelector(selector);
        if (!root) continue;
        const computed = getComputedStyle(root);
        const style: Record<string, string> = {};
        for (const token of SKIN_TOKENS) {
            const value = computed.getPropertyValue(token).trim();
            if (value) style[token] = value;
        }
        return style as CSSProperties;
    }
    return {};
}

/*
 * Cadence guard — the family's own suppression rules (kit §04 cards).
 * In-memory per app session: windowed states remember their last fire,
 * once-per-period states remember `${state}:${periodKey}`.
 */
const lastFiredAt = new Map<CompetitionToastState, number>();
const firedOnce = new Set<string>();

export interface ShowCompetitionToastOptions {
    /**
     * Skip the state's cadence cap for this fire. The kit names the one
     * legitimate use: a "passed" event that changes paid status.
     */
    bypassCadence?: boolean;
    durationMs?: number;
}

/**
 * Fire one of the competition notification family states.
 * Returns true when shown, false when the cadence cap suppressed it.
 */
export function showCompetitionToast(
    state: CompetitionToastState,
    payload: CompetitionToastPayload = {},
    options: ShowCompetitionToastOptions = {},
): boolean {
    const config = competitionToastFamily[state];
    const now = Date.now();

    if (!options.bypassCadence) {
        if (typeof config.cadence === "number") {
            const last = lastFiredAt.get(state) ?? 0;
            if (now - last < config.cadence) return false;
        } else if (config.cadence === "oncePerPeriod") {
            const key = `${state}:${payload.periodKey ?? "current"}`;
            if (firedOnce.has(key)) return false;
            firedOnce.add(key);
        }
    }
    lastFiredAt.set(state, now);

    const duration = options.durationMs ?? DEFAULT_DURATION_MS;
    const skin = readSkinTokens();

    toast.custom(
        (t) => <div
            className={classNames(
                "competition-toast",
                config.severity !== "neutral" && `competition-toast--${config.severity}`,
                !t.visible && "competition-toast--out",
            )}
            style={skin}
        >
            <div className="competition-toast__ico">{config.icon}</div>
            <div className="competition-toast__body">
                <div className="competition-toast__title">{config.title}</div>
                <div className="competition-toast__message">{config.message(payload)}</div>
            </div>
            <button
                className="competition-toast__dismiss"
                aria-label="Dismiss"
                onClick={() => toast.dismiss(t.id)}
            >
                ×
            </button>
            <div
                className="competition-toast__bar"
                style={{animationDuration: `${duration}ms`}}
            />
        </div>,
        {duration},
    );

    return true;
}
